import { useState, useEffect, useRef } from 'react';
import { invoices } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Printer, Download, X, Loader2, Mail, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

// Company Info - TRANSTAR SRL
const COMPANY_INFO = {
  name: 'TRANSTAR SRL',
  rnc: '131-072623',
  address: 'Abraham Lincoln esq. Lope de Vega, Plaza Castilla, Local 1B, Santo Domingo, Republica Dominicana',
  phone: '829-870-0339',
  email: 'info@transtar.io',
  logo: 'https://customer-assets.emergentagent.com/job_teamflow-manage-1/artifacts/krciwypq_2024%20Logo%20TRANSTAR%20.jpg',
};

const NCF_TYPE_NAMES = {
  '01': 'Factura de Crédito Fiscal',
  '02': 'Factura de Consumo',
  '14': 'Régimen Especial Tributario',
  '15': 'Comprobante Gubernamental',
};

export default function InvoicePrint({ invoiceId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    custom_message: ''
  });
  const printRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await invoices.getPdfData(invoiceId);
        setData(res.data);
        // Pre-fill email with client's email
        if (res.data?.client?.email) {
          setEmailForm(prev => ({ ...prev, recipient_email: res.data.client.email }));
        }
      } catch (err) {
        console.error('Error loading invoice:', err);
      } finally {
        setLoading(false);
      }
    };

    if (open && invoiceId) {
      setLoading(true);
      setEmailSent(false);
      loadData();
    }
  }, [open, invoiceId]);

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Factura ${data?.invoice?.invoice_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; color: #333; }
            ${printStyles}
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current || !data) return;
    
    setDownloading(true);
    
    try {
      const element = printRef.current;
      const invoiceNumber = data.invoice.invoice_number.replace(/[^a-zA-Z0-9]/g, '-');
      const clientName = data.client?.name?.replace(/[^a-zA-Z0-9]/g, '-') || 'Cliente';
      const fileName = `Factura-${invoiceNumber}-${clientName}.pdf`;
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: fileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait'
        },
        pagebreak: { mode: 'avoid-all' }
      };

      await html2pdf().set(opt).from(element).save();
      toast.success('PDF descargado exitosamente');
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Error al generar PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    
    if (!emailForm.recipient_email) {
      toast.error('Por favor ingresa un email');
      return;
    }
    
    setSendingEmail(true);
    
    try {
      const response = await invoices.sendEmail(invoiceId, {
        recipient_email: emailForm.recipient_email,
        custom_message: emailForm.custom_message || null
      });
      
      setEmailSent(true);
      toast.success(`Factura enviada a ${emailForm.recipient_email}`);
      
      // Close email dialog after 2 seconds
      setTimeout(() => {
        setShowEmailDialog(false);
        setEmailSent(false);
      }, 2000);
      
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error(err.response?.data?.detail || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (!open) return null;

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <p className="text-center text-muted-foreground">Error al cargar factura</p>
        </DialogContent>
      </Dialog>
    );
  }

  const { invoice, client, company, ncf_info } = data;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 border-b flex flex-row items-center justify-between">
            <DialogTitle className="font-heading text-xl">Vista de Factura</DialogTitle>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setShowEmailDialog(true)}
                variant="default"
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                data-testid="send-email-btn"
              >
                <Mail className="h-4 w-4" />
                Enviar Email
              </Button>
              <Button 
                onClick={handleDownloadPDF} 
                variant="default"
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                disabled={downloading}
                data-testid="download-pdf-btn"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {downloading ? 'Generando...' : 'PDF'}
              </Button>
              <Button onClick={handlePrint} variant="outline" className="gap-2">
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          
          <div ref={printRef} className="p-8 bg-white">
            {/* Header */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <img 
                  src={company.logo} 
                  alt="TRANSTAR" 
                  className="h-20 w-auto mb-2"
                />
                <p className="text-sm text-gray-600 font-semibold">RNC: {company.rnc}</p>
              </div>
              
              <div className="text-right">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">FACTURA</h2>
                <p className="text-lg font-mono font-semibold">{invoice.invoice_number}</p>
              </div>
            </div>

            {/* NCF Section */}
            <div className="bg-[#007AFF] text-white p-4 rounded-lg mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm opacity-90">Número de Comprobante Fiscal (NCF)</p>
                  <p className="text-2xl font-mono font-bold tracking-wider">{invoice.ncf || 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-90">Tipo de Comprobante</p>
                  <p className="font-semibold">{NCF_TYPE_NAMES[invoice.ncf_type] || 'Factura de Consumo'}</p>
                </div>
              </div>
            </div>

            {/* Client & Invoice Info */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2 uppercase text-sm tracking-wide">Facturar a:</h3>
                <p className="font-semibold text-lg">{client?.name}</p>
                {client?.rnc && <p className="text-sm text-gray-600">RNC: {client.rnc}</p>}
                {client?.company && <p className="text-sm text-gray-600">{client.company}</p>}
                {client?.address && <p className="text-sm text-gray-600">{client.address}</p>}
                {client?.email && <p className="text-sm text-gray-600">{client.email}</p>}
                {client?.phone && <p className="text-sm text-gray-600">Tel: {client.phone}</p>}
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2 uppercase text-sm tracking-wide">Detalles:</h3>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha de emisión:</span>
                    <span className="font-medium">{formatDate(invoice.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha de vencimiento:</span>
                    <span className="font-medium">{formatDate(invoice.due_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estado:</span>
                    <Badge variant={invoice.status === 'paid' ? 'success' : 'warning'}>
                      {invoice.status === 'paid' ? 'Pagada' : invoice.status === 'partial' ? 'Pago Parcial' : 'Pendiente'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="p-3 text-left">Descripción</th>
                    <th className="p-3 text-center w-20">Cant.</th>
                    <th className="p-3 text-right w-32">Precio Unit.</th>
                    <th className="p-3 text-right w-32">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items?.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-3">{item.description}</td>
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(item.unit_price)}</td>
                      <td className="p-3 text-right font-mono font-semibold">
                        {formatCurrency(item.quantity * item.unit_price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-72">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-mono">{formatCurrency(invoice.subtotal)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">ITBIS (18%):</span>
                  <span className="font-mono">{formatCurrency(invoice.tax)}</span>
                </div>
                <div className="flex justify-between py-3 bg-[#007AFF] text-white px-3 rounded-lg mt-2">
                  <span className="font-semibold">TOTAL:</span>
                  <span className="font-mono font-bold text-xl">{formatCurrency(invoice.total)}</span>
                </div>
                {invoice.paid_amount > 0 && (
                  <>
                    <div className="flex justify-between py-2 mt-2 border-b">
                      <span className="text-gray-600">Pagado:</span>
                      <span className="font-mono text-green-600">{formatCurrency(invoice.paid_amount)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="font-semibold">Saldo pendiente:</span>
                      <span className="font-mono font-bold text-amber-600">
                        {formatCurrency(invoice.total - invoice.paid_amount)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="bg-gray-50 p-4 rounded-lg mb-8">
                <h3 className="font-semibold text-gray-700 mb-2 uppercase text-sm tracking-wide">Notas:</h3>
                <p className="text-gray-600">{invoice.notes}</p>
              </div>
            )}

            {/* Footer */}
            <div className="border-t pt-6 text-center text-sm text-gray-500">
              <p className="font-semibold mb-1">Gracias por su preferencia!</p>
              <p>Este documento es valido como comprobante fiscal segun la DGII de Republica Dominicana</p>
              <p className="mt-2 text-xs">NCF valido para credito fiscal: {ncf_info?.name}</p>
              <div className="mt-4 pt-3 border-t text-xs text-gray-400">
                <p className="font-medium text-gray-500">TRANSTAR SRL | RNC: {company.rnc}</p>
                <p>{company.address}</p>
                <p>Tel: {company.phone} | {company.email}</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Enviar Factura por Email
            </DialogTitle>
          </DialogHeader>
          
          {emailSent ? (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
              <p className="text-lg font-semibold text-green-700">¡Email Enviado!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Factura enviada a {emailForm.recipient_email}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSendEmail} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="recipient_email">Email del destinatario *</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  placeholder="cliente@email.com"
                  value={emailForm.recipient_email}
                  onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                  required
                  data-testid="email-recipient-input"
                />
                {client?.email && emailForm.recipient_email !== client.email && (
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setEmailForm({ ...emailForm, recipient_email: client.email })}
                  >
                    Usar email del cliente: {client.email}
                  </button>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="custom_message">Mensaje personalizado (opcional)</Label>
                <Textarea
                  id="custom_message"
                  placeholder="Agregue un mensaje para su cliente..."
                  value={emailForm.custom_message}
                  onChange={(e) => setEmailForm({ ...emailForm, custom_message: e.target.value })}
                  rows={3}
                  data-testid="email-message-input"
                />
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Factura:</strong> {invoice?.invoice_number}<br />
                  <strong>NCF:</strong> {invoice?.ncf || 'N/A'}<br />
                  <strong>Total:</strong> {formatCurrency(invoice?.total || 0)}
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEmailDialog(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  disabled={sendingEmail}
                  data-testid="send-email-submit-btn"
                >
                  {sendingEmail ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {sendingEmail ? 'Enviando...' : 'Enviar Factura'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

const printStyles = `
  .p-8 { padding: 2rem; }
  .mb-8 { margin-bottom: 2rem; }
  .mb-6 { margin-bottom: 1.5rem; }
  .mb-2 { margin-bottom: 0.5rem; }
  .mt-2 { margin-top: 0.5rem; }
  .gap-4 { gap: 1rem; }
  .gap-8 { gap: 2rem; }
  .flex { display: flex; }
  .grid { display: grid; }
  .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
  .items-start { align-items: flex-start; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .justify-end { justify-content: flex-end; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .font-bold { font-weight: bold; }
  .font-semibold { font-weight: 600; }
  .font-medium { font-weight: 500; }
  .font-mono { font-family: monospace; }
  .text-2xl { font-size: 1.5rem; }
  .text-3xl { font-size: 1.875rem; }
  .text-xl { font-size: 1.25rem; }
  .text-lg { font-size: 1.125rem; }
  .text-sm { font-size: 0.875rem; }
  .text-xs { font-size: 0.75rem; }
  .uppercase { text-transform: uppercase; }
  .tracking-wide { letter-spacing: 0.025em; }
  .tracking-wider { letter-spacing: 0.05em; }
  .text-white { color: white; }
  .text-gray-800 { color: #1f2937; }
  .text-gray-700 { color: #374151; }
  .text-gray-600 { color: #4b5563; }
  .text-gray-500 { color: #6b7280; }
  .text-green-600 { color: #059669; }
  .text-amber-600 { color: #d97706; }
  .bg-white { background-color: white; }
  .bg-gray-50 { background-color: #f9fafb; }
  .bg-gray-800 { background-color: #1f2937; }
  .rounded-lg { border-radius: 0.5rem; }
  .border { border: 1px solid #e5e7eb; }
  .border-b { border-bottom: 1px solid #e5e7eb; }
  .border-t { border-top: 1px solid #e5e7eb; }
  .p-3 { padding: 0.75rem; }
  .p-4 { padding: 1rem; }
  .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
  .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
  .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
  .pt-6 { padding-top: 1.5rem; }
  .w-full { width: 100%; }
  .w-72 { width: 18rem; }
  .w-20 { width: 5rem; }
  .w-32 { width: 8rem; }
  .h-20 { height: 5rem; }
  .w-auto { width: auto; }
  .space-y-1 > * + * { margin-top: 0.25rem; }
  .opacity-90 { opacity: 0.9; }
  table { border-collapse: collapse; }
  img { max-width: 100%; height: auto; }
  
  [style*="007AFF"] { background-color: #007AFF !important; }
`;
